/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types'
import React from 'react'
import {useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Flex,
    Grid,
    Text,
    VStack
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import ItemVariantProvider from '../../../components/item-variant'
import CartItemVariantImage from '../../../components/item-variant/item-image'
import CartItemVariantPrice from '../../../components/item-variant/item-price'
import CartDrawerQuantityPicker from '../partials/cart-drawer-quantity-picker.jsx'

import CartItemVariantName from '../../../components/item-variant/item-name'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import {colors} from '../../../constants'
/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const CartDrawerProductCard = ({
    product,
    onItemQuantityChange = noop,
    showLoading = false,
    secondaryActions
}) => {
    const {
        stepQuantity,
        quantity,
        setQuantity,
        variationAttributes,
        minOrderQuantity,
        maxOrderQuantity,
        ats
    } = useDerivedProduct(product)
    const intl = useIntl()

    return (
        <Box>
            <Box paddingY="24px" alignItems="start">
                <ItemVariantProvider
                    variant={product}
                    variationAttributes={variationAttributes}
                >
                    {showLoading && <LoadingSpinner />}
                    <Grid
                        gridTemplateColumns="1fr 5fr"
                        alignItems="flex-start"
                        gap="25px"
                    >
                        <CartItemVariantImage width={['88px']} />

                        <Flex
                            justifyContent="space-between"
                            flexDirection="column"
                            gap="16px"
                        >
                            <Flex
                                flexDirection="column"
                                gap="2px"
                                justifyContent="space-between"
                                alignItems="left"
                            >
                                <CartItemVariantName
                                    label={product?.c_extend?.c_brand}
                                    fontFamily="Roboto"
                                    fontWeight="300"
                                    fontSize="12px"
                                    lineHeight="18px"
                                    color={colors.lightGray}
                                />
                                <CartItemVariantName
                                    label={product.name}
                                    fontFamily="Raleway"
                                    fontWeight="400"
                                    fontSize="18px"
                                    lineHeight="24px"
                                />
                                <CartItemVariantPrice
                                    fontWeight="400"
                                    fontSize="14px"
                                    fontFamily="Roboto"
                                    lineHeight="20px"
                                    align="left"
                                />
                            </Flex>
                            <Flex
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <VStack alignItems="flex-start">
                                    <CartDrawerQuantityPicker
                                        step={stepQuantity}
                                        value={quantity}
                                        min={minOrderQuantity}
                                        max={Math.min(maxOrderQuantity, ats)}
                                        clampValueOnBlur={false}
                                        onBlur={(e) => {
                                            // Default to last known quantity if a user leaves the box with an invalid value
                                            const {value} = e.target

                                            if (!value) {
                                                setQuantity(product.quantity)
                                            }
                                        }}
                                        onChange={(
                                            stringValue,
                                            numberValue
                                        ) => {
                                            // Set the Quantity of product to value of input if value number
                                            if (numberValue >= 0) {
                                                // Call handler
                                                onItemQuantityChange(
                                                    numberValue
                                                ).then(
                                                    (isValidChange) =>
                                                        isValidChange &&
                                                        setQuantity(numberValue)
                                                )
                                            } else if (stringValue === '') {
                                                // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                // User will not be able to add '' qauntity to the cart due to the add to cart button enablement rules
                                                setQuantity(stringValue)
                                            }
                                        }}
                                    />
                                    {ats &&
                                        quantity >=
                                            Math.min(maxOrderQuantity, ats) && (
                                            <Text
                                                fontSize="12px"
                                                fontWeight="400"
                                                color={colors.graySolid}
                                            >
                                                {intl.formatMessage({
                                                    id: 'product_view.quantity_limit',
                                                    defaultMessage:
                                                        'Maximum Quantity is reached'
                                                })}
                                            </Text>
                                        )}
                                </VStack>
                                {secondaryActions}
                            </Flex>
                        </Flex>
                    </Grid>
                </ItemVariantProvider>
            </Box>
        </Box>
    )
}

CartDrawerProductCard.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    isWishlistItem: PropTypes.bool,
    primaryAction: PropTypes.node,
    secondaryActions: PropTypes.node
}

export default CartDrawerProductCard
